import {
  ChangeEvent,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { KyrgyzstanEmpty, KyrgyzstanLoader } from 'entities/kyrgyzstan'
import { kyrgyzstanSearchStore } from 'entities/kyrgyzstan/model'

import type { KyrgyzstanCity } from 'shared/api/kyrgyzstan/locations'
import { KyrgyzstanLocationsApi } from 'shared/api/kyrgyzstan/locations'
import { getApiErrorMessage } from 'shared/lib/errorText'
import { Button } from 'shared/ui/button'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { SearchInput } from 'shared/ui/search-input'
import { SelectableCard } from 'shared/ui/selectable-card'

import { KyrgyzstanCities } from './model'
import s from './styles.module.scss'

const KyrgyzstanSearchDirectionPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [cities, setCities] = useState<KyrgyzstanCity[]>([])
  const [selectedCityIds, setSelectedCityIds] = useState<number[]>([])
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const deferredSearch = useDeferredValue(search)

  useEffect(() => {
    setSelectedCityIds(kyrgyzstanSearchStore.cities.map((city) => city.id))
  }, [])

  useEffect(() => {
    let cancelled = false

    const fetchCities = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const { data } = await KyrgyzstanLocationsApi.getCities()
        if (!cancelled) {
          setCities(data.data)
        }
      } catch (err) {
        if (!cancelled) {
          setError(getApiErrorMessage(err) ?? t('kyrgyzstan_block.error.title'))
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchCities()
    return () => {
      cancelled = true
    }
  }, [t])

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleToggleCity = (cityId: number) => {
    setSelectedCityIds((currentIds) =>
      currentIds.includes(cityId)
        ? currentIds.filter((id) => id !== cityId)
        : [...currentIds, cityId],
    )
  }

  const handleApplyCities = () => {
    const selectedCities = cities
      .filter((city) => selectedCityIds.includes(city.id))
      .map((city) => ({
        id: city.id,
        city_name: city.city_name,
        country_code: city.country_code,
        country_name: city.country_name,
        region: city.region,
      }))

    kyrgyzstanSearchStore.setCities(selectedCities)
    navigate(-1)
  }

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value)
  }

  const handleSearchReset = () => {
    setSearch('')
  }

  const normalizedSearch = deferredSearch.trim().toLowerCase()

  const filteredCities = useMemo(() => {
    if (!normalizedSearch) {
      return cities
    }

    return cities.filter((city) =>
      city.city_name.toLowerCase().includes(normalizedSearch),
    )
  }, [cities, normalizedSearch])

  const filteredSoonCities = useMemo(() => {
    if (!normalizedSearch) {
      return KyrgyzstanCities
    }

    return KyrgyzstanCities.filter((city) =>
      city.city_name.toLowerCase().includes(normalizedSearch),
    )
  }, [normalizedSearch])

  const header = (
    <HeaderGoBack
      buttonWhite
      variant="grey"
      title={t('kyrgyzstan_block.search_direction.title')}
      onGoBack={handleGoBack}
    />
  )

  if (isLoading) {
    return (
      <div className={s.container}>
        {header}
        <KyrgyzstanLoader />
      </div>
    )
  }

  if (error) {
    return (
      <div className={s.container}>
        {header}
        <KyrgyzstanEmpty
          title={t('kyrgyzstan_block.error.title')}
          subtitle={error}
        />
      </div>
    )
  }

  if (cities.length === 0) {
    return (
      <div className={s.container}>
        {header}
        <KyrgyzstanEmpty
          title={t('kyrgyzstan_block.empty.title')}
          subtitle={t('kyrgyzstan_block.empty.subtitle')}
        />
      </div>
    )
  }

  return (
    <div className={s.container}>
      {header}
      <div className={`container ${s.content}`}>
        <SearchInput
          size="large"
          placeholder={t('search')}
          onChange={handleSearchChange}
          onReset={handleSearchReset}
        />

        <div className={s.list}>
          {filteredCities.map((city) => {
            const isSelected = selectedCityIds.includes(city.id)

            return (
              <SelectableCard
                key={city.id}
                title={city.city_name}
                isActive={isSelected}
                onClick={() => handleToggleCity(city.id)}
              />
            )
          })}

          {filteredSoonCities.map((city) => (
            <SelectableCard
              key={city.id}
              title={city.city_name}
              isSoon
              soonLabel={t('kyrgyzstan_block.search_direction.soon')}
            />
          ))}

          {filteredCities.length === 0 && filteredSoonCities.length === 0 ? (
            <KyrgyzstanEmpty
              title={t('kyrgyzstan_block.empty.title')}
              subtitle={t('kyrgyzstan_block.empty.subtitle')}
            />
          ) : null}
        </div>
      </div>

      <div className={s.footer}>
        <Button
          className={`${s.applyButton} semi-bold-17`}
          borderRadius="md"
          disabled={selectedCityIds.length === 0}
          onClick={handleApplyCities}
        >
          {t('select_without_date')}
        </Button>
      </div>
    </div>
  )
})

export default KyrgyzstanSearchDirectionPage
